import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Camera, Users, Link as LinkIcon, QrCode, ArrowLeft, Monitor, Trash2, Settings as SettingsIcon, Info as InfoIcon, Clock, MapPin, Unlink } from 'lucide-react';
import toast from 'react-hot-toast';
import { eventsService } from '../../services/firestoreService.js';
import { db } from '../../firebase/config.js';
import { collection, doc, getCountFromServer, onSnapshot, query, where } from 'firebase/firestore';
import EventPhotos from '../Photos/EventPhotos.js';
import EventParticipants from '../Participants/EventParticipants.js';
import { screenManagementService } from '../../services/screenManagementService.js';

const SectionButton = ({ active, onClick, icon: Icon, children }) => (
  <button onClick={onClick} className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 ${active ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50 text-gray-800'}`}>
    {Icon ? <Icon className="w-4 h-4"/> : null}
    <span className="text-sm">{children}</span>
  </button>
);

const InfoSection = ({ event, onSave, saving, setForm }) => {
  const [formData, setFormData] = useState({ name: '', description: '', date: '', time: '', location: '' });
  const [qrOpen, setQrOpen] = useState(false);
  useEffect(() => {
    if (!event) return;
    try {
      const d = event.date?.toDate ? event.date.toDate() : (event.date ? new Date(event.date) : null);
      setFormData({
        name: event.name || '',
        description: event.description || '',
        date: d ? d.toISOString().split('T')[0] : '',
        time: d ? d.toTimeString().slice(0,5) : '',
        location: event.location || ''
      });
    } catch { setFormData({ name: event?.name||'', description: event?.description||'', date:'', time:'', location: event?.location||'' }); }
  }, [event?.eventId, event?.id]);
  useEffect(() => { setForm && setForm(formData); }, [formData]);
  const onChange = (e) => setFormData(s => ({ ...s, [e.target.name]: e.target.value }));
  const copyInviteLink = () => {
    if (!event?.joinCode) return;
    const url = `https://shot.muchpic.com/shot?code=${event.joinCode}`;
    try { navigator.clipboard.writeText(url); toast.success('Enlace copiado'); } catch { toast.success('Enlace listo'); }
  };
  const getQrUrl = () => {
    const target = `https://shot.muchpic.com/shot?code=${encodeURIComponent(event?.joinCode || '')}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=420x420&margin=0&data=${encodeURIComponent(target)}`;
  };
  const downloadQr = async () => {
    const url = getQrUrl();
    try {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) throw new Error('fetch-failed');
      const blob = await res.blob();
      const obj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = obj;
      a.download = `qr-${event?.joinCode || 'evento'}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(obj), 2000);
    } catch (_) {
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = `qr-${event?.joinCode || 'evento'}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch {
        window.open(url, '_blank');
      }
    }
  };
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
        <input name="name" value={formData.name} onChange={onChange} placeholder="Ej: Fiesta de 15 – María" className="input-field py-2.5 bg-gray-50 placeholder-gray-400" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
        <textarea name="description" value={formData.description} onChange={onChange} rows={4} maxLength={280} placeholder="Describe brevemente el evento…" className="input-field min-h-[100px] bg-gray-50 placeholder-gray-400 py-2.5" />
        <div className="mt-1 text-xs text-gray-500">{formData.description.length}/280</div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Fecha</label>
          <div className="relative">
            <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="date" name="date" value={formData.date} onChange={onChange} className="input-field pl-11 py-2.5 bg-gray-50" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Hora</label>
          <div className="relative">
            <Clock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="time" name="time" value={formData.time} onChange={onChange} className="input-field pl-11 py-2.5 bg-gray-50" />
          </div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Ubicación</label>
        <div className="relative">
          <MapPin className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input name="location" value={formData.location} onChange={onChange} placeholder="Ej: Salón Principal" className="input-field pl-11 py-2.5 bg-gray-50 placeholder-gray-400" />
        </div>
      </div>
      {/* Join code y acciones */}
      <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md p-3">
        <div className="text-sm text-gray-800">Código de unión: <span className="font-mono">{event?.joinCode || '—'}</span></div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={copyInviteLink} className="btn-secondary text-xs flex items-center"><LinkIcon className="w-3.5 h-3.5 mr-1"/> Copiar enlace</button>
          <button type="button" onClick={()=>setQrOpen(true)} className="btn-secondary text-xs flex items-center"><QrCode className="w-3.5 h-3.5 mr-1"/> Ver QR</button>
        </div>
      </div>
      <div className="pt-2">
        <button onClick={() => onSave(formData)} disabled={saving} className="btn-primary">{saving ? 'Guardando...' : 'Guardar'}</button>
      </div>
      {qrOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={()=>setQrOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-2xl p-5 w-[min(92vw,520px)]">
            <button onClick={()=>setQrOpen(false)} className="absolute top-2 right-2 p-1.5 rounded hover:bg-gray-100" aria-label="Cerrar">✕</button>
            <h3 className="text-lg font-semibold mb-3">QR para unirse</h3>
            <div className="text-sm text-gray-600 mb-4 break-all">https://shot.muchpic.com/shot?code={event?.joinCode}</div>
            <div className="flex items-center justify-center">
              <img src={getQrUrl()} alt={`QR para ${event?.joinCode}`} className="rounded-md border" style={{ width: 320, height: 320 }} />
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={copyInviteLink} className="btn-secondary text-sm">Copiar enlace</button>
              <button onClick={downloadQr} className="btn-primary text-sm">Descargar QR</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingsSection = ({ event, onSave, saving }) => {
  const [form, setForm] = useState({ statusActive: true, moderation: true, allowUploads: true, showQr: true, slideSeconds: '8', showAuthor: true, pausedImageURL: '', screensaverEnabled: false, screensaverImageURL: '' });
  useEffect(() => {
    if (!event) return;
    setForm({
      statusActive: event?.status === 'active',
      moderation: !(event?.settings?.autoApprove === true),
      allowUploads: !(event?.photoSettings?.allowUploads === false),
      showQr: !(event?.screenSettings?.showQr === false),
      slideSeconds: String(event?.screenSettings?.slideSeconds || 8),
      showAuthor: !(event?.screenSettings?.showAuthor === false),
      pausedImageURL: event?.screenSettings?.pausedImageURL || '',
      screensaverEnabled: !!(event?.screenSettings?.screensaverEnabled === true),
      screensaverImageURL: event?.screenSettings?.screensaverImageURL || ''
    });
  }, [event?.eventId, event?.id]);

  const Switch = ({ checked, onChange, id }) => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={id}
      onClick={() => onChange(!checked)}
      className={`${checked ? 'bg-indigo-600' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none`}
    >
      <span className={`${checked ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}></span>
    </button>
  );

  const Stepper = ({ value, onChange, min = 1, max = 10, id }) => {
    const dec = () => onChange(Math.max(min, value - 1));
    const inc = () => onChange(Math.min(max, value + 1));
    const onKey = (e) => {
      if (e.key === 'ArrowUp') { e.preventDefault(); inc(); }
      if (e.key === 'ArrowDown') { e.preventDefault(); dec(); }
      if (e.key === 'Home') { e.preventDefault(); onChange(min); }
      if (e.key === 'End') { e.preventDefault(); onChange(max); }
    };
    return (
      <div
        role="spinbutton"
        aria-labelledby={id}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        tabIndex={0}
        onKeyDown={onKey}
        className="inline-flex items-center border border-gray-300 rounded-md overflow-hidden"
        style={{ minWidth: 110 }}
      >
        <button
          type="button"
          onClick={dec}
          disabled={value <= min}
          className="px-2 py-1 text-sm bg-gray-50 hover:bg-gray-100 disabled:opacity-50"
        >
          −
        </button>
        <div className="px-3 py-1 text-sm min-w-[44px] text-center select-none">
          {value}s
        </div>
        <button
          type="button"
          onClick={inc}
          disabled={value >= max}
          className="px-2 py-1 text-sm bg-gray-50 hover:bg-gray-100 disabled:opacity-50"
        >
          +
        </button>
      </div>
    );
  };

  const StatusSwitch = ({ checked, onChange, id }) => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={id}
      onClick={() => onChange(!checked)}
      className={`${checked ? 'bg-green-600' : 'bg-red-500'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none`}
    >
      <span className={`${checked ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}></span>
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Estado (arriba) */}
      <ul className="divide-y rounded-md border border-gray-200 bg-white">
        <li className="flex items-center justify-between p-3">
          <div className="pr-3">
            <div id="lab-status" className="text-sm font-medium text-gray-900">Estado del evento</div>
            <div className="text-xs text-gray-600">Activo permite ver y subir según permisos. Inactivo lo oculta.</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${form.statusActive ? 'text-green-700' : 'text-red-600'}`}>{form.statusActive ? 'Activo' : 'Inactivo'}</span>
            <StatusSwitch checked={form.statusActive} onChange={(v)=>setForm(s=>({...s, statusActive:v}))} id="lab-status" />
          </div>
        </li>
      </ul>

      {/* Resto de configuraciones */}
      <ul className="divide-y rounded-md border border-gray-200 bg-white">
        <li className="flex items-center justify-between p-3">
          <div className="pr-3">
            <div id="lab-mod" className="text-sm font-medium text-gray-900">Moderación</div>
            <div className="text-xs text-gray-600">Requerir aprobación antes de mostrar las fotos</div>
          </div>
          <Switch checked={form.moderation} onChange={(v)=>setForm(s=>({...s, moderation:v}))} id="lab-mod" />
        </li>
        <li className="flex items-center justify-between p-3">
          <div className="pr-3">
            <div id="lab-up" className="text-sm font-medium text-gray-900">Permitir subidas</div>
            <div className="text-xs text-gray-600">Habilita que los usuarios puedan subir fotos al evento</div>
          </div>
          <Switch checked={form.allowUploads} onChange={(v)=>setForm(s=>({...s, allowUploads:v}))} id="lab-up" />
        </li>
        <li className="flex items-center justify-between p-3">
          <div className="pr-3">
            <div id="lab-qr" className="text-sm font-medium text-gray-900">Mostrar QR en pantalla</div>
            <div className="text-xs text-gray-600">Muestra el QR de invitación en la app Screen</div>
          </div>
          <Switch checked={form.showQr} onChange={(v)=>setForm(s=>({...s, showQr:v}))} id="lab-qr" />
        </li>
        <li className="flex items-center justify-between p-3">
          <div className="pr-3">
            <div id="lab-author" className="text-sm font-medium text-gray-900">Mostrar autor en pantalla</div>
            <div className="text-xs text-gray-600">Muestra nombre y foto del usuario que subió la foto en la app Screen</div>
          </div>
          <Switch checked={form.showAuthor} onChange={(v)=>setForm(s=>({...s, showAuthor:v}))} id="lab-author" />
        </li>
        <li className="flex items-center justify-between p-3">
          <div className="pr-3">
            <div id="lab-ss" className="text-sm font-medium text-gray-900">Activar protector de pantalla</div>
            <div className="text-xs text-gray-600">Reemplaza el slideshow por una pantalla de MuchPic sin cambiar permisos</div>
          </div>
          <Switch checked={form.screensaverEnabled} onChange={(v)=>setForm(s=>({...s, screensaverEnabled:v}))} id="lab-ss" />
        </li>
        <li className="flex items-center justify-between p-3">
          <div className="pr-3">
            <div id="lab-seg" className="text-sm font-medium text-gray-900">Segundos por foto</div>
            <div className="text-xs text-gray-600">Tiempo de permanencia de cada foto en pantalla</div>
          </div>
          <Stepper
            id="lab-seg"
            value={parseInt(form.slideSeconds) || 8}
            onChange={(v)=>setForm(s=>({...s, slideSeconds: String(v)}))}
            min={1}
            max={10}
          />
        </li>
        <li className="p-3">
          <div className="pr-3">
            <div id="lab-ssimg" className="text-sm font-medium text-gray-900">Imagen del protector de pantalla</div>
            <div className="text-xs text-gray-600 mb-2">Siempre puedes definirla aquí. Si el protector está desactivado, la imagen no se usa. Si se deja vacío, se mostrará una imagen por defecto de MuchPic.</div>
          </div>
          <input
            type="url"
            placeholder="https://.../screensaver.png"
            value={form.screensaverImageURL}
            onChange={(e)=>setForm(s=>({...s, screensaverImageURL:e.target.value }))}
            className="input-field w-full bg-gray-50 placeholder-gray-400"
            aria-labelledby="lab-ssimg"
          />
        </li>
        <li className="p-3">
          <div className="pr-3">
            <div id="lab-pausedimg" className="text-sm font-medium text-gray-900">Imagen cuando el evento está inactivo</div>
            <div className="text-xs text-gray-600 mb-2">URL de una imagen a mostrar en la app Screen mientras el evento está desactivado. Si se deja vacío, se usará una imagen por defecto de MuchPic.</div>
          </div>
          <input
            type="url"
            placeholder="https://.../mi-imagen.png"
            value={form.pausedImageURL}
            onChange={(e)=>setForm(s=>({...s, pausedImageURL:e.target.value }))}
            className="input-field w-full bg-gray-50 placeholder-gray-400"
            aria-labelledby="lab-pausedimg"
          />
        </li>
      </ul>

      <div className="pt-1">
        <button onClick={()=>onSave(form)} disabled={saving} className="btn-primary">{saving ? 'Guardando...' : 'Guardar'}</button>
      </div>
    </div>
  );
};

const ScreensSection = ({ eventId, organizerId }) => {
  const [linkedScreens, setLinkedScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [available, setAvailable] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const q = query(collection(db, 'screens'), where('eventId', '==', eventId));
        const unsub = onSnapshot(q, (snap) => {
          if (!mounted) return;
          const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          setLinkedScreens(list);
        });
        return () => { try { unsub(); } catch {} };
      } finally { setLoading(false); }
    };
    const cleanup = load();
    return () => { try { cleanup && cleanup(); } catch{}; mounted=false; };
  }, [eventId]);

  const openPicker = async () => {
    setPickerOpen(true);
    try {
      const all = await screenManagementService.getAvailableScreens(organizerId || '');
      const avail = (all || []).filter(s => !s.eventId);
      setAvailable(avail);
    } catch { setAvailable([]); }
  };

  const linkOne = async (sc) => {
    if (busy) return;
    setBusy(true); setBusyId(sc.id || sc.screenId);
    try {
      await screenManagementService.linkScreenToEvent(sc.id || sc.screenId, eventId);
      toast.success('Pantalla enlazada');
      setPickerOpen(false);
    } catch { toast.error('No se pudo enlazar'); }
    finally { setBusy(false); setBusyId(null); }
  };

  const unlinkOne = async (sc) => {
    if (busy) return;
    setBusy(true); setBusyId(sc.id || sc.screenId);
    try {
      await screenManagementService.unlinkScreenFromEvent(sc.id || sc.screenId);
      toast.success('Pantalla desvinculada');
    } catch { toast.error('No se pudo desvincular'); }
    finally { setBusy(false); setBusyId(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="font-medium text-gray-900 flex items-center gap-2"><Monitor className="w-4 h-4"/> Pantallas vinculadas</div>
        <button onClick={openPicker} className="btn-primary text-xs flex items-center"><LinkIcon className="w-3.5 h-3.5 mr-1"/> Enlazar pantalla</button>
      </div>
      <ul className="divide-y rounded-md border border-gray-200 bg-white">
        {loading ? (
          <li className="p-3 text-sm text-gray-600">Cargando…</li>
        ) : linkedScreens.length === 0 ? (
          <li className="p-3 text-sm text-gray-600">No hay pantallas vinculadas.</li>
        ) : (
          linkedScreens.map(sc => (
            <li key={sc.id} className="py-2 px-3 flex items-center justify-between">
              <div className="text-sm text-gray-800">
                {sc.name || sc.code || sc.id}
                {sc.code ? (<span className="ml-2 text-xs text-gray-500">({sc.code})</span>) : null}
              </div>
              <button onClick={()=>unlinkOne(sc)} disabled={busy} className="btn-secondary text-xs disabled:opacity-50 flex items-center">
                <Unlink className="w-3.5 h-3.5 mr-1" />
                {busy && busyId===sc.id ? 'Quitando…' : 'Quitar'}
              </button>
            </li>
          ))
        )}
      </ul>

      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={()=>{ if(!busy) setPickerOpen(false); }} />
          <div className="relative bg-white rounded-lg shadow-xl w-[min(92vw,720px)] max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="font-medium">Seleccionar pantalla</div>
              <button onClick={()=>setPickerOpen(false)} disabled={busy} className="text-gray-500 disabled:opacity-50">✕</button>
            </div>
            <div className="p-3 overflow-y-auto">
              {available.length === 0 ? (
                <div className="text-sm text-gray-600">No hay pantallas disponibles.</div>
              ) : (
                <ul className="divide-y">
                  {available.map(sc => (
                    <li key={sc.id || sc.screenId} className="py-2 flex items-center justify-between">
                      <div className="text-sm text-gray-800">
                        {sc.name || sc.code || sc.id}
                        {sc.code ? (<span className="ml-2 text-xs text-gray-500">({sc.code})</span>) : null}
                      </div>
                      <button onClick={()=>linkOne(sc)} disabled={busy} className="btn-primary text-xs disabled:opacity-50 flex items-center">
                        <LinkIcon className="w-3.5 h-3.5 mr-1" />
                        {busy && busyId===(sc.id||sc.screenId) ? 'Enlazando…' : 'Enlazar'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="p-2 border-t text-right">
              <button onClick={()=>setPickerOpen(false)} disabled={busy} className="btn-secondary">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EventManage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('info');
  const [saving, setSaving] = useState(false);
  const [counts, setCounts] = useState({ photos: 0, participants: 0 });

  useEffect(() => {
    let unsub;
    (async () => {
      try {
        setLoading(true);
        // carga inicial (por si el snapshot demora)
        try {
          const ev = await eventsService.getEventById(id);
          if (ev) setEvent(ev);
        } catch {}
        // KPIs
        try {
          const pc = await getCountFromServer(query(collection(db, 'photos'), where('eventId','==', id), where('status','==','approved')));
          const uc = await getCountFromServer(query(collection(db, 'eventParticipants'), where('eventId','==', id)));
          setCounts({ photos: pc.data().count, participants: uc.data().count });
        } catch {}
        // Live updates del evento
        try {
          unsub = onSnapshot(doc(db,'events', id), (snap) => {
            if (snap?.exists()) {
              const d = snap.data();
              setEvent({ id: snap.id, eventId: snap.id, ...d });
            }
          });
        } catch {}
      } finally { setLoading(false); }
    })();
    return () => { try { unsub && unsub(); } catch {} };
  }, [id]);

  const copyInviteLink = () => {
    if (!event?.joinCode) return;
    const url = `https://shot.muchpic.com/shot?code=${event.joinCode}`;
    try { navigator.clipboard.writeText(url); toast.success('Enlace copiado'); } catch { toast.success('Enlace listo'); }
  };

  const saveInfo = async (f) => {
    if (!event) return;
    try {
      setSaving(true);
      const d = f.date ? new Date(`${f.date} ${f.time || '00:00'}`) : null;
      await eventsService.updateEvent(event.eventId || event.id, {
        name: f.name?.trim()||'', description: f.description?.trim()||'',
        date: d && !isNaN(d.getTime()) ? d : null, location: f.location?.trim()||''
      });
      toast.success('Información guardada');
    } catch { toast.error('No se pudo guardar'); }
    finally { setSaving(false); }
  };

  const saveSettings = async (f) => {
    if (!event) return;
    try {
      setSaving(true);
      await eventsService.updateEvent(event.eventId || event.id, {
        status: f.statusActive ? 'active' : 'inactive',
        'photoSettings.moderation': !!f.moderation,
        'photoSettings.allowUploads': !!f.allowUploads,
        'settings.autoApprove': !f.moderation,
        'screenSettings.showQr': !!f.showQr,
        'screenSettings.slideSeconds': Math.min(10, Math.max(1, parseInt(f.slideSeconds)||8)),
        'screenSettings.showAuthor': !!f.showAuthor,
        'screenSettings.pausedImageURL': f.pausedImageURL ? String(f.pausedImageURL) : '',
        'screenSettings.screensaverEnabled': !!f.screensaverEnabled,
        'screenSettings.screensaverImageURL': f.screensaverImageURL ? String(f.screensaverImageURL) : ''
      });
      toast.success('Configuración guardada');
    } catch { toast.error('No se pudo guardar'); }
    finally { setSaving(false); }
  };

  if (loading) return (<div className="p-6"><div className="flex items-center justify-center min-h-96"><div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div></div></div>);

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={()=>navigate('/events')} className="text-indigo-600 hover:text-indigo-700 flex items-center"><ArrowLeft className="w-4 h-4 mr-1"/> Volver</button>
          <h1 className="text-2xl font-bold text-gray-900">Gestionar evento</h1>
        </div>
        {/* <div className="flex items-center gap-2">
          <button onClick={copyInviteLink} className="btn-secondary text-xs flex items-center"><LinkIcon className="w-3.5 h-3.5 mr-1"/> Copiar enlace</button>
          <button onClick={()=>toast('QR disponible en la tarjeta del evento')} className="btn-secondary text-xs flex items-center"><QrCode className="w-3.5 h-3.5 mr-1"/> QR</button>
        </div> */}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Estado</div>
          <div className="mt-1"><span className={`px-2 py-0.5 rounded text-xs font-medium ${event?.status==='active'?'bg-green-100 text-green-800':'bg-gray-100 text-gray-800'}`}>{event?.status==='active'?'Activo':'Inactivo'}</span></div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Fotos aprobadas</div>
          <div className="mt-1 text-2xl font-semibold">{counts.photos}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Participantes</div>
          <div className="mt-1 text-2xl font-semibold">{counts.participants}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Nav izquierda */}
        <div className="bg-white p-3 rounded-lg border border-gray-200 lg:col-span-1 h-fit">
          <div className="font-medium text-gray-900 mb-2">Secciones</div>
          <div className="space-y-1">
            <SectionButton active={active==='info'} onClick={()=>setActive('info')} icon={InfoIcon}>Información del evento</SectionButton>
            <SectionButton active={active==='settings'} onClick={()=>setActive('settings')} icon={SettingsIcon}>Configuraciones</SectionButton>
            <SectionButton active={active==='photos'} onClick={()=>setActive('photos')} icon={Camera}>Fotos</SectionButton>
            <SectionButton active={active==='participants'} onClick={()=>setActive('participants')} icon={Users}>Participantes</SectionButton>
            <SectionButton active={active==='screens'} onClick={()=>setActive('screens')} icon={Monitor}>Pantallas vinculadas</SectionButton>
            <SectionButton active={active==='danger'} onClick={()=>setActive('danger')} icon={Trash2}>Zona de peligro</SectionButton>
          </div>
        </div>

        {/* Panel derecho */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 lg:col-span-3 min-h-[420px]">
          {active==='info' && <InfoSection event={event} onSave={saveInfo} saving={saving} />}
          {active==='settings' && <SettingsSection event={event} onSave={saveSettings} saving={saving} />}
          {active==='photos' && <EventPhotos />}
          {active==='participants' && <EventParticipants />}
          {active==='screens' && <ScreensSection eventId={event?.eventId || event?.id} organizerId={event?.organizerId} />}
          {active==='danger' && (
            <div className="space-y-3">
              <div className="text-sm text-gray-700">Eliminar este evento permanentemente.</div>
              <button onClick={async()=>{ if(!window.confirm('¿Eliminar evento?')) return; try{ await eventsService.deleteEvent(event.eventId || event.id); toast.success('Evento eliminado'); navigate('/events'); } catch{ toast.error('No se pudo eliminar'); } }} className="btn-danger">Eliminar evento</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventManage;
